import React from "react";
import { Navigate, useParams } from "react-router-dom";
//#region redux
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { loadCompaniesAsync } from "store/actions/authActions";
//#endregion
//#region types
import { WintSuperkollAPIModel as ApiModels } from "wint-api-client/dist/types";
//#endregion
//#region components
import { LoadingWithLogoFixed } from "components/common";
import { SomethingWentWrong } from "components/error";
import { LoginContainer } from "components/login";
import useAuthHelper from "utils/helpers/useAuthHelper";
//#endregion

/**
 * Entry point for the share links KAs hand to customers. Resolves the link to the company-scoped
 * report route and gets out of the way.
 *
 * The report endpoint is company-scoped: the SPA only sends a `CompanyId` header once a company is
 * selected, and that only happens under `/:company/*`. A report page reached outside that scope always
 * answered 404 ("Vi hittade ingen rapport med id …"), so both link shapes below redirect into it.
 *
 * - `/MonthlyReports/:companyId/:id` — the current shape. The company is named in the link, so we map
 *   it to that company's url and land the customer directly on the report. No picker, and no way to
 *   end up scoped to the wrong company.
 * - `/MonthlyReports/:id` — links mailed out before the company id was included. Hand off to
 *   `/Company/*`, which resolves the company itself: straight through for a single-company customer,
 *   picker when there are several.
 *
 * When the company lookup itself cannot be completed, this reports an error in place rather than
 * redirecting — see the note on {@link Resolution}.
 */
// Long enough that a slow-but-working request still resolves the company and skips the picker; short
// enough that a hung one doesn't read as a broken page.
const COMPANY_RESOLVE_TIMEOUT_MS = 10_000;

/**
 * What the link's company id resolved to.
 *
 * `not-mine` and `unreachable` are deliberately separate. Both mean we cannot route the customer,
 * but only `not-mine` leaves them somewhere useful: the picker needs the very same company lookup
 * to have succeeded, so sending an `unreachable` customer there produces "Du har flera bolag" above
 * an empty list and a lone log-out button. Distinguishing them is what keeps the fallback honest.
 */
type Resolution =
  | { kind: "resolving" }
  | { kind: "resolved"; companyUrl: string }
  | { kind: "not-mine" }
  | { kind: "unreachable" };

const MonthlyReportEntry: React.FC = () => {
  const { companyId, id = "" } = useParams<{ companyId?: string; id: string }>();
  const { authenticate } = useAuthHelper();
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  // The resolution is stored with the companyId it was resolved FOR, and a resolution for any other
  // company reads as "still resolving" below. Two share links for different companies hit the same
  // route, so React Router re-renders this component with new params instead of remounting it — and
  // render runs before the effect, so resetting the state inside the effect would come too late: the
  // stale render would already have redirected to the previous company's url carrying the new report
  // id. Scoping the customer to the wrong company is the exact bug this route exists to prevent.
  const [resolved, setResolved] = React.useState<{ companyId: string; resolution: Resolution }>();
  const resolution: Resolution =
    resolved && resolved.companyId === companyId ? resolved.resolution : { kind: "resolving" };

  const isAuthenticated = authenticate();
  const pickerPath = `/Company/MonthlyReports/${id}`;

  React.useEffect(() => {
    if (!isAuthenticated || !companyId) return;
    let settled = false;
    // One-shot: whichever of the request and the timeout gets here first decides the outcome. Without
    // this, a request that completes after the timeout would move the customer off an error they had
    // already started reading.
    const settle = (next: Resolution) => {
      if (settled) return;
      settled = true;
      setResolved({ companyId, resolution: next });
    };
    // `loadCompaniesAsync` wraps its request in a promise with no reject path and no catch, so a failed
    // fetch leaves it pending forever — neither `.then` nor `.catch` would ever run. Race it against a
    // timeout so a dead request reports an error instead of parking the customer on a spinner. (The
    // `.catch` below can therefore never fire today; it is there so this keeps working if that action
    // is ever given a reject path.)
    const timeout = window.setTimeout(() => settle({ kind: "unreachable" }), COMPANY_RESOLVE_TIMEOUT_MS);
    dispatch(loadCompaniesAsync() as unknown as Promise<ApiModels.Company[]>)
      .then((companies) => {
        window.clearTimeout(timeout);
        const match = companies.find((c) => c.id === Number(companyId));
        settle(match?.url ? { kind: "resolved", companyUrl: match.url } : { kind: "not-mine" });
      })
      .catch(() => {
        window.clearTimeout(timeout);
        settle({ kind: "unreachable" });
      });
    return () => {
      settled = true;
      window.clearTimeout(timeout);
    };
  }, [isAuthenticated, companyId]);

  // Render the login in place so the URL survives; the effect re-runs once the session lands.
  if (!isAuthenticated) return <LoginContainer />;
  // No company in the link: hand off to the picker, which does its own lookup and reports its own
  // failures. Nothing for us to resolve first.
  if (!companyId) return <Navigate to={pickerPath} replace />;

  switch (resolution.kind) {
    case "resolved":
      return <Navigate to={`/${resolution.companyUrl}/MonthlyReports/${id}`} replace />;
    // The company isn't one of the customer's (stale link, wrong recipient, deactivated company). The
    // lookup worked, so the picker has real options to offer; worst case they pick one and get the
    // standard not-found notice for a report that isn't theirs.
    case "not-mine":
      return <Navigate to={pickerPath} replace />;
    case "unreachable":
      return <SomethingWentWrong />;
    default:
      return <LoadingWithLogoFixed />;
  }
};

export default MonthlyReportEntry;
